import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Notification } from '@core/interfaces/api';
import { formatInTimeZone } from 'date-fns-tz';

export interface NotificationTableProps {
  data: Notification[];
  isLoading: boolean;
  error: Error | null;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  setSortField: (field: string) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  onEdit: (notification: Notification) => void;
  onDelete: (notification: Notification) => void;
  timezone?: string;
}

const columns = [
  { id: 'createdOn', label: 'Date Sent' },
  { id: 'title', label: 'Title' },
  { id: 'text', label: 'Text' },
  { id: 'actions', label: 'Actions' },
];

export const NotificationTable: React.FC<NotificationTableProps> = ({
  data,
  isLoading,
  error,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  onEdit,
  onDelete,
  timezone,
}) => {
  const handleSort = (columnId: string) => {
    const isAsc = sortField === columnId && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(columnId);
  };

  return (
    <TableContainer className="shadow-none border-separate p-4">
      <Table className="min-w-full table-fixed" style={{ maxWidth: '100%' }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id} className="font-medium text-sm">
                {column.id !== 'actions' ? (
                  <TableSortLabel
                    active={sortField === column.id}
                    direction={sortField === column.id ? sortOrder : 'asc'}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                Error loading notifications
              </TableCell>
            </TableRow>
          ) : !data?.length ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No notifications for this property.
              </TableCell>
            </TableRow>
          ) : (
            data.map((n) => (
              <TableRow key={n.id}>
                <TableCell>
                  {n.createdOn ? (
                    <Tooltip
                      title={
                        timezone
                          ? formatInTimeZone(new Date(n.createdOn), timezone, "M/d/yyyy h:mma zzz")
                          : new Date(n.createdOn).toLocaleString()
                      }
                    >
                      <span>
                        {timezone
                          ? formatInTimeZone(new Date(n.createdOn), timezone, 'M/d/yyyy h:mma').toLowerCase()
                          : new Date(n.createdOn).toLocaleString()}
                      </span>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{n.title}</TableCell>
                <TableCell>{n.text}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => onEdit(n)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => onDelete(n)} color="primary">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
